import { useApi, useErrorHandling } from '../../../hooks';
import { useMutation } from '@tanstack/react-query';
import { PasswordWarning } from './PasswordWarning';
import { useFormContext } from 'react-hook-form';
import { Input } from '../../../components';
import { AxiosError } from 'axios';

import type { IVerifyReferenceNumberResponse, IVerifyTechEmailAddressResponse } from '../../../types';
import type { IToggle } from '../../../hooks';
import { useContext, type FC } from 'react';
import { Localization } from '../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../context/LanguageSelectorContext';

interface ITechnicianFieldsProps {
    clientId: number;
    setIsUniqueEmail: IToggle;
    setIsUserNameUnique: IToggle;
}

export const TechnicianFields: FC<ITechnicianFieldsProps> = ({ clientId, setIsUniqueEmail, setIsUserNameUnique }) => {
    const formContext = useFormContext();
    const { verifyTechEmailAddress, verifyTechReferenceNumber } = useApi();
    const { handleApiError } = useErrorHandling();

    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    const verifyReferenceNumberMutation = useMutation({
        mutationFn: verifyTechReferenceNumber,
        onSuccess: (response: IVerifyReferenceNumberResponse | null) => {
            if (response === null || response?.reportingTech?.length === 0) {
                setIsUserNameUnique.on();
                formContext.clearErrors('techReferenceNumber');
            } else {
                if (
                    formContext.getValues('isExistingReportingTech') === true &&
                    response?.reportingTech?.find(
                        (x) => x.emailAddress === formContext.getValues('existingReportingTechEmailAddress')
                    )?.techReferenceNumber !== formContext.getValues('existingReportingTechReferenceNumber')
                ) {
                    setIsUserNameUnique.off();
                    formContext.setError('techReferenceNumber', {
                        message: localization.translate('registration.userNameTaken'),
                    });
                } else {
                    setIsUserNameUnique.on();
                    formContext.clearErrors('techReferenceNumber');
                }
            }
        },
    });

    const verifyTechEmailAddressMutation = useMutation({
        mutationFn: verifyTechEmailAddress,
        onError: (error: Error | AxiosError) => handleApiError(error),
        onSuccess: (response: IVerifyTechEmailAddressResponse | null) => {
            if (response?.reportingTech === null) {
                setIsUniqueEmail.on();
                formContext.clearErrors('techEmailAddress');
            } else {
                if (
                    formContext.getValues('isExistingReportingTech') === true &&
                    response?.reportingTech.emailAddress !== formContext.getValues('existingReportingTechEmailAddress')
                ) {
                    setIsUniqueEmail.off();
                    formContext.setError('techEmailAddress', {
                        message: localization.translate('registration.accountAlreadyExists'),
                    });
                } else {
                    setIsUniqueEmail.on();
                    formContext.clearErrors('techEmailAddress');
                }
            }
        },
    });

    const onBlurEmailAddress = () => {
        const email: string = formContext.getValues('techEmailAddress');
        if (!email || email?.trim().length === 0) return;
        verifyTechEmailAddressMutation.mutate({
            clientId: 0,
            marketId: 0,
            prefixText: email,
        });
    };

    const onBlurReferenceNumber = () => {
        const referenceNumber: string = formContext.getValues('techReferenceNumber');
        if (!referenceNumber || referenceNumber?.trim()?.length === 0) return;
        verifyReferenceNumberMutation.mutate({
            clientId: clientId,
            marketId: 0,
            prefixText: referenceNumber,
        });
    };

    return (
        <div className={'flex flex-col'}>
            {/* TECHNICIAN DETAILS */}
            <div className={'my-6 self-center text-3xl font-medium uppercase text-brand-100'}>
                {localization.translate('registration.technicianDetailsHeader')}
            </div>
            <div className={'flex flex-col space-y-3'}>
                <div className={'flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4'}>
                    {/* TECH FIRST NAME */}
                    <Input
                        inputOptions={{
                            autoComplete: 'first-name',
                            isRequired: true,
                            label: localization.translate('technician.firstName'),
                            name: 'techFirstName',
                        }}
                        tooltip={localization.translate('registration.firstNameTooltip')}
                    />

                    {/* TECH LAST NAME */}
                    <Input
                        inputOptions={{
                            autoComplete: 'last-name',
                            isRequired: true,
                            label: localization.translate('technician.lastName'),
                            name: 'techLastName',
                        }}
                        tooltip={localization.translate('registration.lastNameTooltip')}
                    />
                </div>

                <div className={'flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4'}>
                    {/* TECH REFERENCE NUMBER / USER NAME */}
                    <Input
                        inputOptions={{
                            isRequired: true,
                            label: localization.translate('technician.usernameSpaced'),
                            name: 'techReferenceNumber',
                            onBlur: onBlurReferenceNumber,
                        }}
                        tooltip={localization.translate('registration.usernameTooltip')}
                    />

                    {/* TECH PHONE NUMBER */}
                    <Input
                        inputOptions={{
                            isRequired: true,
                            label: localization.translate('technician.phoneNumber'),
                            name: 'techPhoneNumber',
                        }}
                        tooltip={localization.translate('registration.phoneNumberTooltip')}
                        type={'phone'}
                    />
                </div>

                {formContext.getValues('isExistingB2CLogin') === false && (
                    <>
                        <div className={'flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4'}>
                            {/* TECH EMAIL ADDRESS */}
                            <Input
                                inputOptions={{
                                    isRequired: true,
                                    label: localization.translate('technician.emailAddress'),
                                    name: 'techEmailAddress',
                                    onBlur: onBlurEmailAddress,
                                }}
                                tooltip={localization.translate('registration.emailAddressTooltip')}
                            />

                            {/* TECH CONFIRM EMAIL ADDRESS */}
                            <Input
                                inputOptions={{
                                    isRequired: true,
                                    label: localization.translate('registration.confirmEmailAddress'),
                                    name: 'techConfirmEmailAddress',
                                }}
                                tooltip={localization.translate('registration.confirmEmailAddressTooltip')}
                            />
                        </div>

                        <div className={'flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4'}>
                            {/* TECH PASSWORD */}
                            <Input
                                inputOptions={{
                                    isRequired: true,
                                    label: localization.translate('registration.password'),
                                    name: 'password',
                                    type: 'password',
                                }}
                                tooltip={localization.translate('registration.passwordTooltip')}
                            />

                            {/* TECH CONFIRM PASSWORD */}
                            <Input
                                inputOptions={{
                                    isRequired: true,
                                    label: localization.translate('registration.confirmPassword'),
                                    name: 'confirmPassword',
                                    type: 'password',
                                }}
                                tooltip={localization.translate('registration.confirmPasswordTooltip')}
                            />
                        </div>

                        {/* PASSWORD FORMAT WARNING */}
                        <PasswordWarning />
                    </>
                )}
            </div>
        </div>
    );
};
